// Pure helpers of the field components: price and number text, the date picker's parts, file reading.
// No React, no aliases: runs under `node --test`.
import { normalize_digits, stored_to_jalali, jalali_to_stored, gregorian_to_jalali, parse_stored_date, is_valid_gregorian, FILE_TYPES, LIMITS, DEFAULT_DATE_MODE, DATE_MODES } from "./form_helper.js";

// ---- price and number ---------------------------------------------------------------------------------------------

// 1250000 -> "1,250,000"; the value kept in the form is the number, only the box shows the commas
export const format_price = (value) => {
    if (value === undefined || value === null || value === "") {
        return "";
    }
    const digits = normalize_digits(String(value)).replace(/\D/g, "");
    return digits === "" ? "" : digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// what the visitor typed in the price box -> the number to keep ("" when nothing usable is left)
export const parse_price_input = (text) => {
    const digits = normalize_digits(String(text ?? "")).replace(/\D/g, "");
    if (digits === "") {
        return "";
    }
    const number = Number(digits);
    return Number.isSafeInteger(number) ? number : Number(String(Number.MAX_SAFE_INTEGER));
};

// numbers stay as typed (the server accepts Persian digits and separators); the box only trims what cannot be a number
export const clean_number_input = (text) => String(text ?? "").replace(/[^\d۰-۹٠-٩.\-٫٬,]/g, "");

// ---- dates --------------------------------------------------------------------------------------------------------

export const date_mode_of = (field) => (DATE_MODES.includes(field?.data?.mode) ? field.data.mode : DEFAULT_DATE_MODE);

// a Persian form shows the Jalali calendar, an English one the Gregorian; the stored value is always the ISO text
export const calendar_of = (lang) => (lang === "en" ? "gregorian" : "jalali");

// Esfand 30 exists only in a Jalali leap year; FormHelper's round trip knows which
const jalali_day_exists = (year, month, day) => jalali_to_stored({ year, month, day }, "day") !== null;

export const days_in_month = (calendar, year, month) => {
    if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
        return 31;
    }
    if (calendar === "jalali") {
        if (month <= 6) {
            return 31;
        }
        if (month <= 11) {
            return 30;
        }
        return jalali_day_exists(year, 12, 30) ? 30 : 29;
    }
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
};

// the stored value -> { year, month, day } as shown (parts a mode does not use are undefined)
export const date_parts_from_value = (value, mode, calendar) => {
    const parsed = parse_stored_date(value, mode);
    if (!parsed) {
        return { year: undefined, month: undefined, day: undefined };
    }
    if (calendar === "gregorian") {
        return { year: parsed.year, month: mode === "year" ? undefined : parsed.month, day: mode === "day" ? parsed.day : undefined };
    }
    const shown = stored_to_jalali(value, mode);
    return shown ? { year: shown.year, month: shown.month, day: shown.day } : { year: undefined, month: undefined, day: undefined };
};

// the three selects -> the stored ISO text, or "" while a part the mode needs is still missing
export const value_from_date_parts = (parts, mode, calendar) => {
    const { year, month, day } = parts || {};
    if (!Number.isInteger(year) || (mode !== "year" && !Number.isInteger(month)) || (mode === "day" && !Number.isInteger(day))) {
        return "";
    }
    if (calendar === "gregorian") {
        const pad = (number, length) => String(number).padStart(length, "0");
        if (mode === "year") {
            return pad(year, 4);
        }
        if (mode === "month") {
            return `${pad(year, 4)}-${pad(month, 2)}`;
        }
        return is_valid_gregorian(year, month, day) ? `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}` : "";
    }
    return jalali_to_stored({ year, month, day }, mode) || "";
};

// the years offered: `data.min_year` .. `data.max_year` when the admin set them, otherwise a window around today
export const year_options = (calendar, field, today = new Date()) => {
    const current = calendar === "jalali"
        ? gregorian_to_jalali(today.getUTCFullYear(), today.getUTCMonth() + 1, today.getUTCDate())[0]
        : today.getUTCFullYear();
    const min = Number.isInteger(field?.data?.min_year) ? field.data.min_year : current - 100;
    const max = Number.isInteger(field?.data?.max_year) ? field.data.max_year : current + 10;
    const years = [];
    for (let year = max; year >= min; year -= 1) {
        years.push(year);
    }
    return years;
};

// ---- files --------------------------------------------------------------------------------------------------------

export const file_extension_of = (name) => {
    const text = String(name ?? "");
    const dot = text.lastIndexOf(".");
    return dot < 0 ? "" : text.slice(dot + 1).toLowerCase();
};

// the checks that can be made before reading the file: its name, its mime type and its size (spec §8: jpg/png/webp/gif/pdf/docx;
// `max_bytes` is the server's 5 MB unless the caller passes the smaller limit the transport allows, see DEFAULT_MAX_FILE_BYTES)
export const check_file = (file, max_bytes = LIMITS.FILE_MAX_BYTES) => {
    if (!file || typeof file.name !== "string") {
        return { code: "file_invalid" };
    }
    const extension = file_extension_of(file.name);
    if (!Object.prototype.hasOwnProperty.call(FILE_TYPES, extension)) {
        return { code: "file_type_not_allowed", detail: Object.keys(FILE_TYPES).join(",") };
    }
    if (file.type && !FILE_TYPES[extension].includes(String(file.type).toLowerCase())) {
        return { code: "file_type_not_allowed", detail: Object.keys(FILE_TYPES).join(",") };
    }
    if (!Number.isFinite(file.size) || file.size <= 0) {
        return { code: "file_invalid" };
    }
    if (file.size > max_bytes) {
        return { code: "file_too_large", detail: String(max_bytes) };
    }
    return null;
};

// File -> the value the form keeps and the server takes: { content (data url), file_name, is_base_64: true, size }.
// `reader_factory` is injected so the logic can be tested; the browser passes `() => new FileReader()`.
export const read_file_value = (file, reader_factory = () => new FileReader()) => new Promise((resolve, reject) => {
    const reader = reader_factory();
    reader.onload = () => resolve({ content: String(reader.result), file_name: file.name, is_base_64: true, size: file.size });
    reader.onerror = () => reject(new Error("file_read_failed"));
    reader.readAsDataURL(file);
});

export const format_file_size = (bytes) => {
    const number = Number(bytes);
    if (!Number.isFinite(number) || number < 0) {
        return "";
    }
    return number >= 1024 * 1024 ? `${Math.round((number / (1024 * 1024)) * 10) / 10} MB` : `${Math.max(1, Math.round(number / 1024))} KB`;
};
